import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useGet } from '../hooks/useApi';
import { GiftRow } from '../models/gifts/gift.model';
import {
  CheckState,
  InboxItem,
  InboxSeverity,
  SEVERITY_RANK,
  SystemCheck,
} from '../models/inbox/inbox.model';
import { BalanceStatus } from '../models/ops/vtpass.model';
import { IncomingTransferRow, ProviderCheck } from '../models/profit/profit.model';
import { ReconciliationHealth, RiskAlert, RiskSummary, RISK_RULES } from '../models/risk/risk.model';
import {
  CUSTOMERS,
  FEEDBACK,
  GIFTS,
  PROFIT,
  RECONCILIATION,
  RISK_CENTER,
  TRANSACTIONS,
} from '../routes/routeConstants';
import { formatNaira, formatPhone, timeAgo } from '../utils/utils';

const POLL_MS = 60_000;
// An unconfirmed purchase becomes an Inbox item after this long; before that
// reconciliation is still doing its job on its own.
const STUCK_PURCHASE_MINUTES = 60;
// The risk scan runs every 5 minutes; older than this means it has stopped.
const SCAN_STALE_MINUTES = 20;

type Page<T> = { records: T[]; totalRecords: number };

interface ReconQueueItem {
  reference: string;
  chat_id: string;
  type: string;
  stage: string;
  amount: string;
  provider: string;
  age_minutes: number;
  escalated: boolean;
  created_at: string;
}

interface FeedbackRow {
  id: string;
  created_at: string;
  user_name: string | null;
  chat_id: string | null;
  comment: string | null;
  topic: string | null;
  transaction_reference: string | null;
}

interface InboxContextValue {
  items: InboxItem[];
  checks: SystemCheck[];
  // Alerts someone has acknowledged and is looking into.
  inProgress: number;
  loaded: boolean;
  updatedAt: Date | null;
  refresh: () => void;
}

const InboxContext = createContext<InboxContextValue | null>(null);

const naira = (v?: string | number | null) => formatNaira(Number(v ?? 0));
const riskSeverity: Record<string, InboxSeverity> = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
};
const minutesSince = (iso?: string | null) =>
  iso ? (Date.now() - new Date(iso).getTime()) / 60000 : Infinity;
const customer = (chatId?: string | null, name?: string | null) =>
  name ? `${name} · ${formatPhone(chatId)}` : formatPhone(chatId);

// Polls every source once a minute for the whole dashboard (sidebar badge,
// status light, Overview and the Inbox page share one copy).
export const InboxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const vtpass = useGet<BalanceStatus>();
  const health = useGet<ReconciliationHealth>();
  const riskSummary = useGet<RiskSummary>();
  const alerts = useGet<Page<RiskAlert>>();
  const acknowledged = useGet<Page<RiskAlert>>();
  const queue = useGet<Page<ReconQueueItem>>();
  const failedRefunds = useGet<Page<GiftRow>>();
  const failedDeliveries = useGet<Page<GiftRow>>();
  const badFeedback = useGet<Page<FeedbackRow>>();
  const providerChecks = useGet<ProviderCheck[]>();
  const transferRefunds = useGet<Page<IncomingTransferRow>>();
  const transferRefundsFailed = useGet<Page<IncomingTransferRow>>();
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const fetchAll = useRef<() => void>(() => {});
  fetchAll.current = () => {
    vtpass.fetch('api/v1/admin/vtpass/balance-status');
    health.fetch('api/v1/admin/reconciliation/health');
    riskSummary.fetch('api/v1/admin/risk/summary', { window: '24h' });
    alerts.fetch('api/v1/admin/risk/alerts', { status: 'OPEN', window: '30d', page: 0, size: 50 });
    acknowledged.fetch('api/v1/admin/risk/alerts', { status: 'ACKNOWLEDGED', window: '30d', page: 0, size: 1 });
    queue.fetch('api/v1/admin/reconciliation/queue', { page: 0, size: 50 });
    failedRefunds.fetch('api/v1/admin/gifts', { refund_status: 'FAILED', page: 0, size: 50 });
    failedDeliveries.fetch('api/v1/admin/gifts', { status: 'DELIVERY_FAILED', page: 0, size: 50 });
    badFeedback.fetch('api/v1/admin/feedback', { status: 'NEW', rating: 1, page: 0, size: 50 });
    providerChecks.fetch('api/v1/admin/profit/provider-checks');
    transferRefunds.fetch('api/v1/admin/transfers', { status: 'NEEDS_ACCOUNT', page: 0, size: 50 });
    transferRefundsFailed.fetch('api/v1/admin/transfers', { status: 'REFUND_FAILED', page: 0, size: 50 });
    setUpdatedAt(new Date());
  };

  const refresh = useCallback(() => fetchAll.current(), []);

  useEffect(() => {
    refresh();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  const value = useMemo<InboxContextValue>(() => {
    const items: InboxItem[] = [];
    const h = health.data;
    const v = vtpass.data;

    // --- System -----------------------------------------------------------
    if (h?.worker_alive === false) {
      items.push({
        id: 'worker-down',
        kind: 'system',
        severity: 'critical',
        title: 'The background worker isn’t running',
        why: 'WhatsApp messages, scheduled runs and reconciliation are all paused until it’s restarted.',
        meta: ['Restart the arq worker'],
        since: h.generated_at,
        action: { label: 'Open Reconciliation', to: `/${RECONCILIATION}` },
      });
    }
    if (h && h.ledger_imbalanced_transactions > 0) {
      items.push({
        id: 'ledger',
        kind: 'system',
        severity: 'critical',
        title: `${h.ledger_imbalanced_transactions} transaction(s) don’t balance in the ledger`,
        why: 'Money moved without matching entries. Check these before anything else.',
        meta: [`Debits ${naira(h.ledger_total_debits)} · credits ${naira(h.ledger_total_credits)}`],
        since: h.generated_at,
        action: { label: 'Open Reconciliation', to: `/${RECONCILIATION}` },
      });
    }
    if (v?.state && v.state !== 'OK') {
      const hours = v.hours_left != null ? Math.max(1, Math.round(Number(v.hours_left))) : null;
      items.push({
        id: 'vtpass',
        kind: 'system',
        severity: v.state === 'LOW' ? 'high' : 'critical',
        title:
          v.state === 'UNREADABLE'
            ? 'We can’t read the VTpass wallet'
            : v.state === 'CRITICAL'
              ? 'The VTpass wallet is almost empty'
              : 'The VTpass wallet is running low',
        why:
          v.state === 'UNREADABLE'
            ? 'Check the VTpass account and API keys. Purchases may fail without warning.'
            : 'Top it up on VTpass. When it runs out, every purchase fails.',
        meta: [
          v.balance != null ? `Balance ${naira(v.balance)}` : '',
          hours ? `about ${hours}h left at the current pace` : '',
        ].filter(Boolean),
        since: v.checked_at,
        action: { label: 'See settings', to: `/${PROFIT}` },
      });
    }
    (h?.providers ?? [])
      .filter((p) => p.status !== 'HEALTHY')
      .forEach((p) =>
        items.push({
          id: `provider-${p.provider}`,
          kind: 'system',
          // Down, or failing right now, is urgent; a low success rate over the
          // day with nothing failing this hour can wait.
          severity: p.status === 'DOWN' ? 'critical' : p.failures_last_hour > 0 ? 'high' : 'medium',
          title: p.status === 'DOWN' ? `${p.provider} looks down` : `${p.provider} purchases are often failing`,
          why: `${p.successful} of ${p.attempts} purchase${p.attempts === 1 ? '' : 's'} succeeded in the last 24 hours${
            p.failures_last_hour > 0 ? `, ${p.failures_last_hour} failed in the last hour` : ''
          }. Check VTpass’s status for this product.`,
          meta: [`${p.success_rate.toFixed(0)}% success`, p.unresolved ? `${p.unresolved} unconfirmed` : ''].filter(Boolean),
          since: h?.generated_at ?? null,
          action: { label: 'Open Reconciliation', to: `/${RECONCILIATION}` },
        })
      );

    // --- Risk --------------------------------------------------------------
    (alerts.data?.records ?? []).forEach((a) =>
      items.push({
        id: `risk-${a.id}`,
        kind: 'risk',
        severity: riskSeverity[a.severity] ?? 'medium',
        title: a.title || RISK_RULES[a.rule] || a.rule,
        why: a.detail,
        meta: [
          a.chat_id ? formatPhone(a.chat_id) : '',
          Number(a.amount_at_risk) > 0 ? `${naira(a.amount_at_risk)} at risk` : '',
          RISK_RULES[a.rule] ?? '',
        ].filter(Boolean),
        since: a.first_detected_at,
        action: {
          label: 'Review alert',
          to: `/${RISK_CENTER}?search=${encodeURIComponent(a.transaction_reference ?? a.chat_id ?? '')}`,
        },
        chatId: a.chat_id,
      })
    );

    // --- Purchases still being confirmed -------------------------------------
    (queue.data?.records ?? [])
      .filter((q) => q.escalated || q.age_minutes >= STUCK_PURCHASE_MINUTES)
      .forEach((q) => {
        const refund = q.stage === 'reversal';
        items.push({
          id: `recon-${q.reference}`,
          kind: 'purchase',
          severity: q.escalated || q.age_minutes > 6 * 60 ? 'high' : 'medium',
          title: refund
            ? `Refund owed for ${q.type.toLowerCase().replace('_', ' ')}`
            : `Unconfirmed ${q.type.toLowerCase().replace('_', ' ')} purchase`,
          why: refund
            ? 'The customer is waiting for their money back.'
            : q.stage === 'charge'
              ? 'We don’t know yet if the card was charged. If it was, reconciliation refunds it.'
              : 'The card was charged, but we don’t know yet if VTpass delivered. Reconcile it now, or check with VTpass.',
          meta: [formatPhone(q.chat_id), naira(q.amount), q.provider],
          since: q.created_at,
          action: { label: 'Open purchase', to: `/${TRANSACTIONS}?search=${encodeURIComponent(q.reference)}` },
          chatId: q.chat_id,
        });
      });

    // --- Dash gifts ----------------------------------------------------------
    (failedRefunds.data?.records ?? []).forEach((g) =>
      items.push({
        id: `gift-refund-${g.id}`,
        kind: 'gift',
        severity: 'high',
        title: `Refund to the sender failed (${g.code})`,
        why: 'Gift refunds are never retried automatically. Open the gift and press Retry refund.',
        meta: [formatPhone(g.sender_chat_id), naira(Number(g.amount ?? 0) + Number(g.fee ?? 0))],
        since: g.created_at,
        action: { label: 'Open gift', to: `/${GIFTS}?search=${encodeURIComponent(g.code)}` },
        chatId: g.sender_chat_id,
      })
    );
    (failedDeliveries.data?.records ?? []).forEach((g) =>
      items.push({
        id: `gift-delivery-${g.id}`,
        kind: 'gift',
        severity: 'medium',
        title: `Gift couldn’t be delivered (${g.code})`,
        why: 'The recipient hasn’t received it yet. Try Deliver now, or check the details they gave.',
        meta: [`to ${formatPhone(g.recipient_chat_id)}`, naira(g.amount)],
        since: g.created_at,
        action: { label: 'Open gift', to: `/${GIFTS}?search=${encodeURIComponent(g.code)}` },
        chatId: g.recipient_chat_id,
      })
    );

    // --- Bank transfers (pricing spec T5) ------------------------------------
    [...(transferRefunds.data?.records ?? []), ...(transferRefundsFailed.data?.records ?? [])].forEach((t) =>
      items.push({
        id: `transfer-refund-${t.id}`,
        kind: 'transfer',
        severity: 'high',
        title:
          t.status === 'NEEDS_ACCOUNT'
            ? `Bank transfer refund needs the sender’s account (${naira(t.amount)})`
            : `Bank transfer refund failed (${naira(t.amount)})`,
        why:
          t.status === 'NEEDS_ACCOUNT'
            ? 'Paystack can’t send it back without the bank account. In Paystack: Refunds → Needs attention, add the sender’s account and retry.'
            : 'The money is still with us. Refund it from Paystack (Transactions → this reference → Refund), or contact the customer.',
        meta: [t.paystack_reference, t.sender_name ?? 'unknown sender', t.chat_id ? formatPhone(t.chat_id) : 'no customer'],
        since: t.created_at,
        action: t.chat_id
          ? { label: 'Open customer', to: `/${CUSTOMERS}/${t.chat_id}` }
          : { label: 'Open Profit', to: `/${PROFIT}` },
        chatId: t.chat_id ?? undefined,
      })
    );

    // --- Unhappy customers ---------------------------------------------------
    (badFeedback.data?.records ?? []).forEach((f) =>
      items.push({
        id: `feedback-${f.id}`,
        kind: 'feedback',
        severity: 'low',
        title: 'A customer had a bad experience',
        why: f.comment ? `“${f.comment.slice(0, 140)}${f.comment.length > 140 ? '…' : ''}”` : 'They rated it Bad without a comment.',
        meta: [customer(f.chat_id, f.user_name), f.topic ? f.topic.replace(/_/g, ' ') : ''].filter(Boolean),
        since: f.created_at,
        action: { label: 'Read feedback', to: `/${FEEDBACK}` },
        chatId: f.chat_id,
      })
    );

    // --- Costs that don't match the provider's bill --------------------------
    (providerChecks.data ?? [])
      .filter((c) => c.flagged)
      .forEach((c) =>
        items.push({
          id: `cost-${c.provider}-${c.day}`,
          kind: 'costs',
          severity: 'low',
          title: `${c.provider === 'meta' ? 'WhatsApp' : c.provider === 'anthropic' ? 'Claude' : c.provider} costs don’t match their bill`,
          why: `Our records differ from what they reported by ${c.difference_pct ?? '?'}% on ${c.day}. Profit figures may be off.`,
          meta: [`Theirs ${naira(c.reported_cost_ngn)}`, `ours ${naira(c.our_cost_ngn)}`],
          since: c.checked_at,
          action: { label: 'Open Profit', to: `/${PROFIT}` },
        })
      );

    items.sort(
      (a, b) =>
        SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
        (a.since ? new Date(a.since).getTime() : Infinity) - (b.since ? new Date(b.since).getTime() : Infinity)
    );

    // --- System status (the light in the sidebar) ----------------------------
    const lastScan = riskSummary.data?.last_scan_at ?? null;
    const unhealthy = (h?.providers ?? []).filter((p) => p.status !== 'HEALTHY');
    const checks: SystemCheck[] = [
      {
        key: 'worker',
        label: 'Background worker',
        state: !h ? 'unknown' : h.worker_alive === null ? 'unknown' : h.worker_alive ? 'ok' : 'down',
        detail: !h || h.worker_alive === null ? 'Can’t tell (Redis unreachable)' : h.worker_alive ? 'Running' : 'Not running',
      },
      {
        key: 'vtpass',
        label: 'VTpass wallet',
        state: !v?.state ? 'unknown' : v.state === 'OK' ? 'ok' : v.state === 'LOW' ? 'warn' : 'down',
        detail: v?.balance != null ? `${naira(v.balance)}${v.state && v.state !== 'OK' ? ` · ${v.state.toLowerCase()}` : ''}` : 'No reading yet',
      },
      {
        key: 'providers',
        label: 'Providers',
        state: !h ? 'unknown' : unhealthy.some((p) => p.status === 'DOWN') ? 'down' : unhealthy.length ? 'warn' : 'ok',
        detail: !h ? '—' : unhealthy.length ? unhealthy.map((p) => `${p.provider} ${p.status.toLowerCase()}`).join(', ') : 'All healthy',
      },
      {
        key: 'reconciliation',
        label: 'Reconciliation',
        state: !h ? 'unknown' : h.escalated_count > 0 ? 'warn' : 'ok',
        detail: !h
          ? '—'
          : h.unresolved_count === 0
            ? 'Nothing waiting'
            : `${h.unresolved_count} being confirmed (${naira(h.unresolved_amount)})`,
      },
      {
        key: 'risk',
        label: 'Risk scan',
        state: !lastScan ? 'unknown' : minutesSince(lastScan) > SCAN_STALE_MINUTES ? 'warn' : 'ok',
        detail: lastScan ? `Last ran ${timeAgo(lastScan)}` : 'Hasn’t run yet',
      },
      {
        key: 'ledger',
        label: 'Ledger',
        state: !h ? 'unknown' : h.ledger_imbalanced_transactions > 0 ? 'down' : 'ok',
        detail: !h ? '—' : h.ledger_imbalanced_transactions > 0 ? `${h.ledger_imbalanced_transactions} out of balance` : 'Balanced',
      },
    ];

    return {
      items,
      checks,
      inProgress: acknowledged.data?.totalRecords ?? 0,
      loaded: !!(h || alerts.data || vtpass.data),
      updatedAt,
      refresh,
    };
  }, [
    vtpass.data,
    health.data,
    riskSummary.data,
    alerts.data,
    acknowledged.data,
    queue.data,
    failedRefunds.data,
    failedDeliveries.data,
    badFeedback.data,
    providerChecks.data,
    transferRefunds.data,
    transferRefundsFailed.data,
    updatedAt,
    refresh,
  ]);

  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
};

export const useInbox = () => {
  const ctx = useContext(InboxContext);
  if (!ctx) throw new Error('useInbox must be used inside InboxProvider');
  return ctx;
};

export const overallState = (checks: SystemCheck[]): CheckState =>
  checks.some((c) => c.state === 'down')
    ? 'down'
    : checks.some((c) => c.state === 'warn')
      ? 'warn'
      : checks.every((c) => c.state === 'unknown')
        ? 'unknown'
        : 'ok';

export const customerPath = (chatId: string) => `/${CUSTOMERS}/${chatId}`;
